/* AddNewUserRoleRenderer.js */

"use strict";

const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const ButtonControl = uiCore.Control.Action.Button;
const InputTextControl = uiCore.Control.Textfield.InputText;
const ComboBoxControl = uiCore.Control.OptionSelection.Single.ComboBox;
const MODAL_TITLE_ADD = "Add New Role";
const editDialog = require("../addEditUserRole/AddEditUserRoleComponent");

class AddNewUserRoleRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = this._createRootElement();

        this._errorContainer = document.createElement("div");
        this._errorContainer.classList.add("error");

        /* form controls */
        this._roleNameText = new InputTextControl({
            darkTheme: true,
            invalid: false,
            placeholder: "Name",
        });

        this._copyFrom = new ComboBoxControl({
            showFilter: false,
            adjustWidth: true,
            darkTheme: true,
        });

        this.cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
            disabled: false,
        });
        this.createButton = new ButtonControl({
            label: "Create",
            darkTheme: true,
            disabled: true,
            active: true,
        });

        /* form controls */

        this._registerListeners();
        this.render();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS */
    /* START PRIVATE COMPONENT RENDERER METHODS */

    /********************************************/

    _registerListeners() {
        this._model.on("Error", this._onError.bind(this));

        this.cancelButton.on("UserClick", this.cancelAction.bind(this));
        this.createButton.on("UserClick", this.submitAction.bind(this));
        this._roleNameText.on("UserValue", this._onRoleNameChange.bind(this));
    }

    _unregisterListeners() {
        this._model.removeAllListeners("Error");
        this.cancelButton.removeAllListeners("UserClick");
        this.createButton.removeAllListeners("UserClick");
        this._roleNameText.removeAllListeners("UserValue");
    }

    _render() {
        const root = document.createDocumentFragment();
        const title = document.createElement("h2");
        title.innerText = MODAL_TITLE_ADD;

        return this._model.getRoles().then((roles) => {
            root.appendChild(title);
            root.appendChild(this._createForm(roles));

            return new Promise((resolve /*,reject*/) => {
                this._rootElem.appendChild(root);
                resolve();
            });
        });
    }

    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;

        return Promise.all([])
            .then(() => {
                this._model = undefined;
                this._shutdownProm = undefined;
                this._errorContainer.remove();
                this._errorContainer = undefined;
                this._renderProm = undefined;

                this._roleNameText = undefined;
                this._copyFrom = undefined;
                this.cancelButton = undefined;
                this.createButton = undefined;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PRIVATE RENDERER METHODS */

    /*******************************************/

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("add-new-user-role", "modal_feedback_container");
        rootElement.component = this._model;
        return rootElement;
    }

    _createForm(roles) {
        const formRoot = document.createDocumentFragment();
        const roleNameLabel = document.createElement("label");
        roleNameLabel.innerText = "Name";
        roleNameLabel.title = "Name the role";

        const roleCopyFromLabel = document.createElement("label");
        roleCopyFromLabel.innerText = "Copy from";
        roleCopyFromLabel.title =
            "(Optional) Select another role that you want to base the new role on";

        formRoot.appendChild(roleNameLabel);
        formRoot.appendChild(this._roleNameText.renderer.getRootElement());

        formRoot.appendChild(roleCopyFromLabel);
        formRoot.appendChild(this._copyFrom.renderer.getRootElement());

        const actionsContainer = document.createElement("div");
        actionsContainer.classList.add("actions-container");
        actionsContainer.appendChild(this.cancelButton.renderer.getRootElement());
        actionsContainer.appendChild(this.createButton.renderer.getRootElement());

        this._copyFrom.options = roles;
        this._copyFrom.setSelectedOption("none");

        formRoot.appendChild(actionsContainer);

        formRoot.appendChild(this._errorContainer);

        return formRoot;
    }

    /*******************************************/
    /* END PRIVATE RENDERER METHODS  */
    /* START HANDLER METHODS */
    /*******************************************/

    submitAction() {
        this._model
            .saveActionGroup(this._copyFrom.value, this._roleNameText.value)
            .then(() => {
                const component = new editDialog({
                    manageRoleComponent: this.model._manageRoleComponent,
                    entityDef: this.model._actionGroupEntityDef,
                    actionGroupEntity: this.model._actionGroupEntity,
                    userFeedback: this.model.userFeedback,
                    copyFrom: this._copyFrom.value,
                    activeOrgId: this.model.activeOrgId,
                    customFieldsWithInherited: this.model._customFieldsWithInherited,
                    deviceEntity: this.model._deviceEntity,
                    uiUtils: this.model._uiUtils,
                    descendantOrgs: this.model._descendantOrgs,
                });

                this.model.userFeedback
                    .displayCustom(component, {notDismissable: false})
                    .catch((error) => {
                        if (error && !error.userRejected) {
                            console.log(
                                "AddNewUserRoleRenderer._openRoleEditor() Error in _openRoleEditor",
                                error
                            );
                        }
                    });
            })
            .catch((err) => {
                console.log("AddNewUserRoleRenderer.submitAction() Error in saveActionGroup", err);
            });
    }

    cancelAction() {
        this._model.closePopUp();
    }

    _onError(errorMsg) {
        this._setError(errorMsg);
    }

    _onRoleNameChange(e) {
        this._model._actionGroupEntity.setName(e);
        this._enableDisableCreateButton(_.isEmpty(e));
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _enableDisableCreateButton(state) {
        this.createButton.setDisabled(state);
    }

    _setError(msg) {
        this._errorContainer.innerText = msg;
        this.focusInput();
    }

    focusInput() {
        this._roleNameText.focus();
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = AddNewUserRoleRenderer;
